import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Prohibit, WhatsappLogo, SignOut } from "@phosphor-icons/react";

export default function Banned() {
  const { user, logout } = useAuth();
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [appeals, setAppeals] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/appeals/mine");
      setAppeals(data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!message.trim()) {
      toast.error("Please explain why we should unban you");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/appeals", { message: message.trim() });
      toast.success("Appeal submitted. Admin will review.");
      setMessage("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit appeal");
    } finally {
      setSubmitting(false);
    }
  };

  const hasPending = appeals.some((a) => a.status === "pending");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6 flex items-center justify-center">
      <div className="max-w-lg w-full space-y-6">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto">
            <Prohibit size={38} weight="fill" className="text-red-400" />
          </div>
          <h1 className="font-heading text-3xl font-black">Account banned</h1>
          <p className="text-white/60">
            Hi {user?.name}, your account has been suspended. You can submit an appeal below or contact admin.
          </p>
        </div>

        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Submit an appeal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {hasPending ? (
              <p className="text-yellow-400 text-sm">
                You already have a pending appeal. Admin will respond soon.
              </p>
            ) : (
              <>
                <Textarea
                  data-testid="appeal-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Explain the situation, why you believe the ban should be reversed…"
                  rows={5}
                  className="bg-white/5 border-white/10 text-white"
                />
                <Button
                  data-testid="appeal-submit-btn"
                  onClick={submit}
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Submit appeal
                </Button>
              </>
            )}

            {appeals.length > 0 && (
              <div className="pt-4 border-t border-white/10 space-y-2">
                <p className="text-xs uppercase tracking-widest font-bold text-white/50">
                  Your past appeals
                </p>
                {appeals.map((a) => (
                  <div key={a.appeal_id} className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-xs text-white/50">
                        {new Date(a.created_at).toLocaleString()}
                      </p>
                      <Badge
                        variant="outline"
                        className={
                          a.status === "approved"
                            ? "border-emerald-500/40 text-emerald-400"
                            : a.status === "rejected"
                            ? "border-red-500/40 text-red-400"
                            : "border-yellow-500/40 text-yellow-400"
                        }
                      >
                        {a.status}
                      </Badge>
                    </div>
                    <p className="text-sm mt-2">{a.message}</p>
                    {a.admin_note && (
                      <p className="text-xs text-white/50 mt-2 italic">
                        Admin: {a.admin_note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <a
            data-testid="banned-contact-admin"
            href="https://wa.me/233539025750"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 h-11 rounded-md bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold transition-colors"
          >
            <WhatsappLogo size={20} weight="fill" />
            Contact Admin
          </a>
          <Button
            data-testid="banned-logout-btn"
            onClick={logout}
            variant="outline"
            className="flex-1 border-white/20 bg-transparent hover:bg-white/5 gap-2"
          >
            <SignOut size={16} weight="bold" /> Log out
          </Button>
        </div>
      </div>
    </div>
  );
}
